import { useState, useEffect } from 'react';

interface DeliverySimulatorProps {
  onPriceUpdate: (price: number) => void;
  onAddressUpdate: (address: string) => void;
}

export default function DeliverySimulator({ onPriceUpdate, onAddressUpdate }: DeliverySimulatorProps) {
  const [address, setAddress] = useState('');
  const [distance, setDistance] = useState(0);
  const [carSize, setCarSize] = useState('');
  const [wheelCoating, setWheelCoating] = useState('');
  const [glassCoating, setGlassCoating] = useState('');

  // 出張洗車専用の基本料金テーブル（研磨なしコースとは別）
  const deliveryBasePrices = {
    ss: 10000,
    s: 12000,
    m: 14000,
    l: 16000,
    ll: 18000
  };

  // 岐阜県内の詳細距離データ（番地レベルまで対応）
  const detailedDistanceData: { [key: string]: number } = {
    // 岐阜市内（詳細番地）
    '岐阜市薮田南1': 27,
    '岐阜市薮田南2': 28,
    '岐阜市薮田南3': 28,
    '岐阜市薮田南4': 29,
    '岐阜市薮田南5': 28,
    '岐阜市薮田南6': 27,
    '岐阜市柳津町1': 24,
    '岐阜市柳津町2': 25,
    '岐阜市柳津町3': 26,
    '岐阜市茜部1': 25,
    '岐阜市茜部2': 26,
    '岐阜市茜部3': 27,
    '岐阜市六条1': 26,
    '岐阜市六条2': 27,
    '岐阜市六条3': 28,
    '岐阜市長良1': 29,
    '岐阜市長良2': 30,
    '岐阜市長良3': 31,
    '岐阜市金華1': 28,
    '岐阜市金華2': 29,
    '岐阜市金華3': 30,
    '岐阜市鷺山1': 27,
    '岐阜市鷺山2': 28,
    '岐阜市鷺山3': 29,
    '岐阜市島1': 23,
    '岐阜市島2': 24,
    '岐阜市島3': 25,
    
    // 大垣市内（詳細番地）
    '大垣市郭町1': 34,
    '大垣市郭町2': 35,
    '大垣市郭町3': 36,
    '大垣市中川町1': 35,
    '大垣市中川町2': 36,
    '大垣市中川町3': 37,
    '大垣市林町1': 37,
    '大垣市林町2': 38,
    '大垣市林町3': 39,
    
    // 各務原市内（詳細番地）
    '各務原市那加1': 17,
    '各務原市那加2': 18,
    '各務原市那加3': 19,
    '各務原市蘇原1': 19,
    '各務原市蘇原2': 20,
    '各務原市蘇原3': 21,
    '各務原市鵜沼1': 21,
    '各務原市鵜沼2': 22,
    '各務原市鵜沼3': 23,
    
    // 可児市内（詳細番地）
    '可児市広見1': 11,
    '可児市広見2': 12,
    '可児市広見3': 13,
    '可児市今渡1': 9,
    '可児市今渡2': 10,
    '可児市今渡3': 11,
    '可児市下恵土1': 14,
    '可児市下恵土2': 15,
    '可児市下恵土3': 16,
    
    // 美濃加茂市内（詳細番地）
    '美濃加茂市太田町1': 2,
    '美濃加茂市太田町2': 3,
    '美濃加茂市太田町3': 4,
    '美濃加茂市本郷町1': 4,
    '美濃加茂市本郷町2': 5,
    '美濃加茂市本郷町3': 6,
    '美濃加茂市古井町1': 3,
    '美濃加茂市古井町2': 4,
    '美濃加茂市古井町3': 5,
    '美濃加茂市加茂野町1': 1,
    '美濃加茂市加茂野町2': 2,
    '美濃加茂市加茂野町3': 3,
    
    // 関市内（詳細番地）
    '関市小屋名1': 11,
    '関市小屋名2': 12,
    '関市小屋名3': 13,
    '関市山田1': 17,
    '関市山田2': 18,
    '関市山田3': 19,
    
    // 多治見市内（詳細番地）
    '多治見市本町1': 44,
    '多治見市本町2': 45,
    '多治見市本町3': 46,
    
    // 基本市区町村（番地が特定できない場合）
    '岐阜市': 28,
    '大垣市': 35,
    '各務原市': 20,
    '可児市': 12,
    '美濃加茂市': 4,
    '関市': 15,
    '多治見市': 45,
    '高山市': 80,
    '中津川市': 65,
    '美濃市': 20,
    '瑞浪市': 50,
    '羽島市': 30,
    '恵那市': 60,
    '土岐市': 40,
    '山県市': 30,
    '瑞穂市': 25,
    '飛騨市': 90,
    '本巣市': 25,
    '郡上市': 40,
    '下呂市': 70,
    '海津市': 40
  };

  const carSizes = [
    { id: 'ss', name: 'SS（軽自動車）' },
    { id: 's', name: 'S（コンパクトカー）' },
    { id: 'm', name: 'M（プリウス、mazda3など）' },
    { id: 'l', name: 'L（大型セダン、中型SUVなど）' },
    { id: 'll', name: 'LL（大型SUV、ミニバン）' }
  ];

  const wheelCoatingOptions = [
    { id: 'wheel-15-17', name: 'ホイールコーティング（〜17インチ）', price: 6000 },
    { id: 'wheel-18-20', name: 'ホイールコーティング（18〜20インチ）', price: 6900 },
    { id: 'wheel-21-plus', name: 'ホイールコーティング（21インチ〜）', price: 7800 }
  ];

  const glassCoatingOptions = [
    { id: 'glass-front', name: '窓ガラスコーティング（フロントガラスのみ）', price: 4000 },
    { id: 'glass-3-sides', name: '窓ガラスコーティング（3面ガラス）', price: 6000 },
    { id: 'glass-all', name: '窓ガラスコーティング（全てのガラス）', price: 11000 }
  ];

  // 住所から詳細距離を計算（番地レベルまで対応）
  const calculateDetailedDistance = (inputAddress: string) => {
    if (!inputAddress) {
      setDistance(0);
      return;
    }

    // 親コンポーネントに住所を通知
    onAddressUpdate(inputAddress);

    // 遅延を追加してリアルな計算感を演出
    setTimeout(() => {
      let foundDistance = 0;
      
      // 1. 最も詳細な番地レベルで検索
      for (const [location, dist] of Object.entries(detailedDistanceData)) {
        if (inputAddress.includes(location)) {
          foundDistance = dist;
          break;
        }
      }

      // 2. 番地が見つからない場合、市区町村名 + 番地数字で推定
      if (foundDistance === 0) {
        const cityMatch = inputAddress.match(/(.*?[市区町村])/);
        const numberMatch = inputAddress.match(/(\d+)/g);
        
        if (cityMatch && numberMatch) {
          const cityName = cityMatch[1];
          const firstNumber = parseInt(numberMatch[0]);
          
          // 市区町村の基本距離を取得
          const baseDistance = detailedDistanceData[cityName] || 30;
          
          // 番地の数字に基づいて微調整（1-3: -1km, 4-6: 基本, 7-9: +1km, 10+: +2km）
          let adjustment = 0;
          if (firstNumber <= 3) adjustment = -1;
          else if (firstNumber >= 7 && firstNumber <= 9) adjustment = 1;
          else if (firstNumber >= 10) adjustment = 2;
          
          foundDistance = Math.max(1, baseDistance + adjustment);
        }
      }

      // 3. それでも見つからない場合、住所の文字数と含まれる数字から推定
      if (foundDistance === 0) {
        const addressLength = inputAddress.length;
        const numbers = inputAddress.match(/\d+/g);
        const numberCount = numbers ? numbers.length : 0;
        
        // 住所の複雑さから距離を推定
        let estimatedDistance = 25; // 基本距離
        
        // 住所が長いほど遠い可能性
        if (addressLength > 20) estimatedDistance += 5;
        if (addressLength > 30) estimatedDistance += 5;
        
        // 番地の数が多いほど詳細な住所（市内の可能性）
        if (numberCount >= 3) estimatedDistance -= 3;
        if (numberCount >= 5) estimatedDistance -= 5;
        
        // 特定のキーワードで調整
        if (inputAddress.includes('町') || inputAddress.includes('丁目')) estimatedDistance -= 2;
        if (inputAddress.includes('字') || inputAddress.includes('大字')) estimatedDistance += 3;
        
        foundDistance = Math.max(1, estimatedDistance);
      }

      setDistance(foundDistance);
    }, 800); // 0.8秒の計算時間を演出
  };

  // 総料金を計算
  const calculateTotalPrice = () => {
    if (!carSize) return 0;

    // 出張洗車専用の基本料金
    const basePrice = deliveryBasePrices[carSize as keyof typeof deliveryBasePrices] || 0;

    // 出張費（距離 × 160円）
    const deliveryFee = distance * 160;

    // オプション料金
    let optionPrice = 0;
    
    if (wheelCoating) {
      const wheelOption = wheelCoatingOptions.find(o => o.id === wheelCoating);
      optionPrice += wheelOption?.price || 0;
    }
    
    if (glassCoating) {
      const glassOption = glassCoatingOptions.find(o => o.id === glassCoating);
      optionPrice += glassOption?.price || 0;
    }

    return Math.round(basePrice + deliveryFee + optionPrice);
  };

  useEffect(() => {
    calculateDetailedDistance(address);
  }, [address]);

  useEffect(() => {
    onPriceUpdate(calculateTotalPrice());
  }, [distance, carSize, wheelCoating, glassCoating, onPriceUpdate]);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-xl font-bold text-gray-800 mb-6">出張洗車シミュレーター</h3>
      
      <div className="space-y-6">
        {/* 住所入力 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            お客様の住所
          </label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="例：東京都渋谷区渋谷1-1-1"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        {/* 距離表示 */}
        {distance > 0 && (
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">拠点からの距離</span>
              <span className="text-lg font-bold text-green-600">{distance.toFixed(1)} km</span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-gray-700">出張費</span>
              <span className="text-lg font-bold text-green-600">{(distance * 160).toLocaleString()}円</span>
            </div>
          </div>
        )}

        {/* コース説明 */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-start">
            <i className="ri-information-line text-blue-600 text-xl mr-3 mt-1"></i>
            <div>
              <p className="font-semibold text-gray-800 mb-1">出張洗車(簡易コーティング)コース</p>
              <p className="text-sm text-gray-600">出張洗車は簡易コーティングコースのみとなります</p>
            </div>
          </div>
        </div>

        {/* 車のサイズ選択 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            車のサイズ
          </label>
          <select
            value={carSize}
            onChange={(e) => setCarSize(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
          >
            <option value="">車のサイズを選択してください</option>
            {carSizes.map((size) => (
              <option key={size.id} value={size.id}>
                {size.name}
              </option>
            ))}
          </select>
        </div>

        {/* ホイールコーティング選択 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ホイールコーティング
          </label>
          <select
            value={wheelCoating}
            onChange={(e) => setWheelCoating(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
          >
            <option value="">選択しない</option>
            {wheelCoatingOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name} - {option.price.toLocaleString()}円
              </option>
            ))}
          </select>
        </div>

        {/* 窓ガラスコーティング選択 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            窓ガラスコーティング
          </label>
          <select
            value={glassCoating}
            onChange={(e) => setGlassCoating(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-8"
          >
            <option value="">選択しない</option>
            {glassCoatingOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name} - {option.price.toLocaleString()}円
              </option>
            ))}
          </select>
        </div>

        {/* 合計金額表示 */}
        <div className="bg-green-50 rounded-xl p-6 text-center border-2 border-green-200">
          <div className="text-sm text-gray-600 mb-2">出張洗車合計金額</div>
          <div className="text-3xl font-bold text-green-600">
            {calculateTotalPrice() > 0 ? `${calculateTotalPrice().toLocaleString()}円` : '---'}
          </div>
          {calculateTotalPrice() > 0 && carSize && (
            <div className="mt-4 pt-4 border-t border-green-200 text-left space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">基本料金</span>
                <span className="font-semibold text-gray-800">
                  {deliveryBasePrices[carSize as keyof typeof deliveryBasePrices].toLocaleString()}円
                </span>
              </div>
              {distance > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">出張費（{distance.toFixed(1)}km × 160円）</span>
                  <span className="font-semibold text-gray-800">
                    {(distance * 160).toLocaleString()}円
                  </span>
                </div>
              )}
              {(wheelCoating || glassCoating) && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">オプション料金</span>
                  <span className="font-semibold text-gray-800">
                    {(() => {
                      let optionTotal = 0;
                      if (wheelCoating) {
                        const wheelOption = wheelCoatingOptions.find(o => o.id === wheelCoating);
                        optionTotal += wheelOption?.price || 0;
                      }
                      if (glassCoating) {
                        const glassOption = glassCoatingOptions.find(o => o.id === glassCoating);
                        optionTotal += glassOption?.price || 0;
                      }
                      return optionTotal.toLocaleString();
                    })()}円
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="text-xs text-gray-500 mt-3">
            ※実際の料金は車の状態により変動する場合があります
          </div>
        </div>
      </div>
    </div>
  );
}
